import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';

import { RangeUnit, rangeUnitList, WeatherWindowForm } from './types';
import { Measurement, measurementList, weatherMap } from '../weather/types';
import { useLocations } from 'features/locations/api/getLocations';
import { useWeatherWall } from './api/getWeatherWall';
import { useUpdateWeatherWindow } from './api/updateWeatherWindow';
import { useCreateWeatherWindow } from './api/createWeatherWindow';

interface Props {
  open: boolean;
  handleClose: () => void;
  wallId: number;
  xPos: number;
  yPos: number;
}

const WeatherWindowDialog = ({
  open,
  handleClose,
  wallId,
  xPos,
  yPos,
}: Props) => {
  const { data: locations } = useLocations({});
  const { data: wall } = useWeatherWall({ wallId });
  const updateWindow = useUpdateWeatherWindow({});
  const createWindow = useCreateWeatherWindow({});
  const window = wall?.weatherWindows.find(
    (weatherWindow) =>
      weatherWindow.pos_x === xPos && weatherWindow.pos_y === yPos
  );
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<WeatherWindowForm>({
    defaultValues: {
      pos_x: xPos,
      pos_y: yPos,
      weather_wall_id: wallId,
      location_id: '',
      measurement: '',
      range_unit: 'days',
      range: '1',
      forecast: 'past',
    },
  });

  useEffect(() => {
    if (window) {
      reset();
      setValue('pos_x', xPos);
      setValue('pos_y', yPos);
      setValue('weather_wall_id', window.weather_wall_id);
      setValue('location_id', '' + window.location_id);
      setValue('measurement', window.measurement);
      setValue('range_unit', window.range_unit);
      setValue('range', '' + window.range);
      setValue('forecast', window.forecast ? 'future' : 'past');
    } else {
      reset();
      setValue('pos_x', xPos);
      setValue('pos_y', yPos);
      setValue('weather_wall_id', wallId);
    }
  }, [window, wallId, open, xPos, yPos, reset, setValue]);

  const onSubmit = async () => {
    const formState = getValues();
    const newWindow = {
      ...window,
      ...formState,
      forecast: formState.forecast === 'past' ? false : true,
    };
    const newWindowId = newWindow.id;
    if (!newWindow.measurement) return;
    if (newWindowId) {
      await updateWindow.mutateAsync({
        windowId: newWindowId,
        data: {
          ...newWindow,
          location_id: Number(formState.location_id),
          measurement: newWindow.measurement,
          range: Number(newWindow.range),
        },
      });
    } else {
      const { location_id, measurement, range, range_unit } = newWindow;
      if (!location_id || !measurement) return;
      await createWindow.mutateAsync({
        ...newWindow,
        location_id: Number(location_id),
        measurement,
        range: range ? Number(range) : 1,
        range_unit: range_unit ? range_unit : 'days',
        weather_wall_id: wallId,
      });
    }
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>Edit Window</DialogTitle>
      <DialogContent>
        <Grid
          item
          container
          direction='column'
          spacing={1}
          style={{ padding: '1rem' }}
        >
          <Grid item>
            <Controller
              name='location_id'
              control={control}
              rules={{
                required: true,
                validate: (value) => Number(value) !== 0,
              }}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth variant='outlined'>
                  <InputLabel required id='location-select-label'>
                    Location
                  </InputLabel>
                  <Select
                    labelId='location-select-label'
                    id='location-select'
                    label='Location'
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    placeholder='None'
                    error={!!errors.location_id}
                  >
                    <MenuItem value={0}>
                      <em>None</em>
                    </MenuItem>
                    {locations?.map((location) => {
                      return (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item>
            <Controller
              name='measurement'
              control={control}
              rules={{
                required: true,
                validate: (value) => Number(value) !== 0,
              }}
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth variant='outlined'>
                  <InputLabel required id='measurement-select-label'>
                    Measurement
                  </InputLabel>
                  <Select
                    labelId='measurement-select-label'
                    id='measurement-select'
                    label='Measurement'
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder='None'
                    error={!!errors.measurement}
                  >
                    <MenuItem value={0}>
                      <em>None</em>
                    </MenuItem>
                    {measurementList.map((measurementName: Measurement) => {
                      return (
                        <MenuItem
                          key={weatherMap[measurementName].data}
                          value={weatherMap[measurementName].data}
                        >
                          {measurementName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs>
              <Controller
                name='range'
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    type='number'
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    fullWidth
                    error={!!errors.range}
                    disabled={watch('forecast') === 'future'}
                    inputProps={{ min: '1' }}
                  >
                    Range Number
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs>
              <Controller
                name='range_unit'
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl variant='outlined' fullWidth>
                    <InputLabel id='range-select-label'>Time Unit</InputLabel>
                    <Select
                      labelId='range-select-label'
                      id='range-select'
                      label='Time Unit'
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      placeholder='None'
                      fullWidth
                      error={!!errors.range_unit}
                      disabled={watch('forecast') === 'future'}
                    >
                      {rangeUnitList.map((range: RangeUnit) => {
                        return (
                          <MenuItem key={range} value={range}>
                            {range}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
          <Grid item>
            <Controller
              name='forecast'
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <FormLabel required error={!!errors.forecast} id='type-label'>
                    Window Type
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby='type-label'
                    defaultValue='Past Conditions'
                    name='radio-buttons-group'
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    <Grid container>
                      <FormControlLabel
                        value='past'
                        control={<Radio />}
                        label='Past Conditions'
                      />
                      <FormControlLabel
                        value='future'
                        control={<Radio />}
                        label='Forecast'
                      />
                    </Grid>
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant='contained' color='secondary'>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty}
          variant='contained'
          color='primary'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeatherWindowDialog;
